import { useCallback, useMemo } from 'react'
import { type UseFormGetValues, type UseFormSetValue } from 'react-hook-form'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { DEFAULT_ADDITIONAL_OBJECTS_LIMIT, serviceIdRequiresIdFieldForUpdates, IntegrationServiceId } from '../../../../common/integrations'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import {
  AccountIntegrationConfig,
  CRMAccountIntegrationConfig,
  type DetailedCRMAccountIntegrationMapping,
  getAccountCRMIntegrationConfig,
  MappingType,
  OperationType,
} from '../../../../models/Account'
import { IntegrationSettings } from './schema'
import { defaultCRMAccountIntegrationConfig } from './AdminSettingsTabs/model'
import { useGetMeQuery } from '../../../../store/user/user'
import { useTracking } from '../../../../apps/professional-app/fa/common/tracking/hooks'
import { areMappingsEqual } from '../../utils'
import { TrackEvent } from '../../../../apps/professional-app/fa/common/tracking'
import { useGetAllEnabledWorkflowsQuery } from '../../../../store/workflow/enabledWorkflows'
import { WORKFLOWS } from '../../../Workflows/WorkflowMapping'
import { BusinessEventExportWorkflowContext } from '../../../../models/Workflow'

export interface FormFieldDefinition {
  name: string
  label: string
  required: boolean
  type: string
  extraTypeInfo: string | null
  maxLength: number
}

export interface IntegrationObjectRelationship {
  referenceTo: string
  relationshipName: string
  label: string
}

export const useGetIntegrationAdminSettings = (serviceId: IntegrationServiceId) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching, isLoading: isAccountLoading } = useGetAccountQuery({ accountId })
  const [triggerUpdateAccount, { isLoading: isAccountUpdating }] = useUpdateAccountMutation()
  const { trackingClient } = useTracking()

  const currentIntegrationAccountConfig = useMemo(() => {
    if (!account) {
      return null
    }
    return getAccountCRMIntegrationConfig(account.data, serviceId) || null
  }, [account, serviceId])

  const { data: userData } = useGetMeQuery({ accountId: accountId })

  const hasIntegration = useMemo(() => {
    if (!userData || !userData.integrations) {
      return false
    }
    return !!userData.integrations.some((i) => i.serviceId === serviceId)
  }, [userData, serviceId])

  const saveAccountMappingConfig = useCallback(
    async (integrationConfig: IntegrationSettings) => {
      if (!account) {
        return
      }

      const existingIntegrations = account.data.effectiveConfig.integrations ?? []

      const currentIntegration = getAccountCRMIntegrationConfig(account.data, serviceId) || defaultCRMAccountIntegrationConfig(serviceId)

      const otherIntegrations = existingIntegrations.filter((config) => config.serviceId !== serviceId)

      const updatedIntegration: CRMAccountIntegrationConfig = {
        ...currentIntegration,
        ...integrationConfig,
        type: 'crm',
        mappings: integrationConfig.mappings as unknown as CRMAccountIntegrationConfig['mappings'],
        enableNoteExport: integrationConfig.enableNoteExport ?? currentIntegration.enableNoteExport,
        enableFullMeetingExport: integrationConfig.enableFullMeetingExport ?? currentIntegration.enableFullMeetingExport,
        isSyncDisabled: integrationConfig.isSyncDisabled ?? currentIntegration.isSyncDisabled,
        isCustom: integrationConfig.isCustom ?? currentIntegration.isCustom,
      }

      if (updatedIntegration.mappings.session?.mappingType === MappingType.PREDEFINED) {
        if (updatedIntegration.mappings.session?.objectName !== currentIntegration.mappings.session?.objectName) {
          trackingClient.track({
            eventName: TrackEvent.PREDEFINED_INTEGRATION_CONFIG_SESSION_OBJECT_MODIFIED,
            eventDetails: {
              serviceId,
              previousObjectName: currentIntegration.mappings.session?.objectName,
              newObjectName: updatedIntegration.mappings.session?.objectName,
            },
          })
        }
        if (
          updatedIntegration.mappings.session?.additionalInformation?.saveToHousehold !==
          currentIntegration.mappings.session?.additionalInformation?.saveToHousehold
        ) {
          trackingClient.track({
            eventName: TrackEvent.PREDEFINED_INTEGRATION_CONFIG_SAVE_TO_HOUSEHOLD_TOGGLED,
            eventDetails: {
              serviceId,
              value: updatedIntegration.mappings.session?.additionalInformation?.saveToHousehold ? 'enabled' : 'disabled',
            },
          })
        }
      } else if (currentIntegration.mappings.session?.mappingType === MappingType.CUSTOM) {
        if (updatedIntegration.mappings.session?.objectName !== currentIntegration.mappings.session?.objectName) {
          trackingClient.track({
            eventName: TrackEvent.CUSTOM_INTEGRATION_CONFIG_SESSION_OBJECT_MODIFIED,
            eventDetails: {
              serviceId,
              previousObjectName: currentIntegration.mappings.session?.objectName,
              newObjectName: updatedIntegration.mappings.session?.objectName,
            },
          })
        }
        if (
          updatedIntegration.mappings.session?.operationType === OperationType.UPDATE &&
          updatedIntegration.mappings.session?.operationType !== currentIntegration.mappings.session?.operationType
        ) {
          trackingClient.track({
            eventName: TrackEvent.CUSTOM_INTEGRATION_CONFIG_UPDATE_MEETING_SUMMARY_CHECKED,
            eventDetails: {
              serviceId,
            },
          })
        }
        if (updatedIntegration.mappings.session?.isHousehold !== currentIntegration.mappings.session?.isHousehold) {
          trackingClient.track({
            eventName: TrackEvent.CUSTOM_INTEGRATION_CONFIG_SESSION_SAVE_TO_HOUSEHOLD_TOGGLED,
            eventDetails: {
              serviceId,
              value: updatedIntegration.mappings.session?.isHousehold ? 'enabled' : 'disabled',
            },
          })
        }
      }

      if (updatedIntegration.mappings.contact?.mappingType === MappingType.PREDEFINED) {
        if (updatedIntegration.mappings.contact?.objectName !== currentIntegration.mappings.contact?.objectName) {
          trackingClient.track({
            eventName: TrackEvent.PREDEFINED_INTEGRATION_CONFIG_CONTACT_OBJECT_MODIFIED,
            eventDetails: {
              serviceId,
              previousObjectName: currentIntegration.mappings.contact?.objectName,
              newObjectName: updatedIntegration.mappings.contact?.objectName,
            },
          })
        }

        if (updatedIntegration.mappings.contact?.fields?.email?.[0]?.value !== currentIntegration.mappings.contact?.fields?.email?.[0]?.value) {
          trackingClient.track({
            eventName: TrackEvent.PREDEFINED_INTEGRATION_CONFIG_EMAIL_FIELD_FILLED,
            eventDetails: {
              serviceId,
              emailField: updatedIntegration.mappings.contact?.fields?.email?.[0]?.value,
            },
          })
        }

        if (updatedIntegration.mappings.contact?.fields?.household?.[0]?.value !== currentIntegration.mappings.contact?.fields?.household?.[0]?.value) {
          trackingClient.track({
            eventName: TrackEvent.PREDEFINED_INTEGRATION_CONFIG_HOUSEHOLD_FIELD_FILLED,
            eventDetails: {
              serviceId,
              householdNameField: updatedIntegration.mappings.contact?.fields?.household?.[0]?.value,
            },
          })
        }
      } else if (currentIntegration.mappings.contact?.mappingType === MappingType.CUSTOM) {
        if (updatedIntegration.mappings.contact?.objectName !== currentIntegration.mappings.contact?.objectName) {
          trackingClient.track({
            eventName: TrackEvent.CUSTOM_INTEGRATION_CONFIG_CONTACT_OBJECT_MODIFIED,
            eventDetails: {
              serviceId,
              previousObjectName: currentIntegration.mappings.contact?.objectName,
              newObjectName: updatedIntegration.mappings.contact?.objectName,
            },
          })
        }
        if (
          updatedIntegration.mappings.contact?.filters?.length !== currentIntegration.mappings.contact?.filters?.length &&
          updatedIntegration.mappings.contact?.filters?.length
        ) {
          trackingClient.track({
            eventName: TrackEvent.CUSTOM_INTEGRATION_CONFIG_CONTACT_FILTERS_MODIFIED,
            eventDetails: {
              serviceId,
              previousFiltersCount: currentIntegration.mappings.contact?.filters?.length,
              newFiltersCount: updatedIntegration.mappings.contact?.filters?.length,
            },
          })
        }
        const additionalFields = Object.keys(updatedIntegration.mappings.contact?.fields || []).filter((key) => {
          if (key === 'email' || key === 'name' || key === 'first_name' || key === 'last_name') {
            return false
          }
          return true
        })

        if (additionalFields.length > 0) {
          trackingClient.track({
            eventName: TrackEvent.CUSTOM_INTEGRATION_CONFIG_CONTACT_UPDATE_CONFIGURED,
            eventDetails: {
              serviceId,
              additionalFields,
            },
          })
        }
      }

      if (updatedIntegration.mappings.task?.mappingType === MappingType.CUSTOM) {
        if (updatedIntegration.mappings.task?.objectName !== currentIntegration.mappings.task?.objectName) {
          trackingClient.track({
            eventName: TrackEvent.CUSTOM_INTEGRATION_CONFIG_TASK_OBJECT_MODIFIED,
            eventDetails: {
              serviceId,
              previousObjectName: currentIntegration.mappings.task?.objectName,
              newObjectName: updatedIntegration.mappings.task?.objectName,
            },
          })
        }
        if (updatedIntegration.mappings.task?.isHousehold !== currentIntegration.mappings.task?.isHousehold) {
          trackingClient.track({
            eventName: TrackEvent.CUSTOM_INTEGRATION_CONFIG_TASK_SAVE_TO_HOUSEHOLD_TOGGLED,
            eventDetails: {
              serviceId,
              value: updatedIntegration.mappings.task?.isHousehold ? 'enabled' : 'disabled',
            },
          })
        }
      }

      const integrations: AccountIntegrationConfig[] = [...otherIntegrations, updatedIntegration]
      const config = {
        ...account.data.config,
        integrations,
      }
      await triggerUpdateAccount({
        accountId,
        payload: {
          config,
        },
      }).unwrap()
    },
    [account, serviceId, accountId, triggerUpdateAccount, trackingClient],
  )

  const requiresIdFieldForUpdates = serviceIdRequiresIdFieldForUpdates(serviceId)

  return {
    isAccountUpdating,
    isAccountFetching,
    isAccountLoading,
    saveAccountMappingConfig,
    currentIntegrationAccountConfig,
    hasIntegration,
    requiresIdFieldForUpdates,
  }
}

export const useSyncAdditionalObjects = (getValues: UseFormGetValues<IntegrationSettings>, setValue: UseFormSetValue<IntegrationSettings>) => {
  const syncAdditionalObjects = useCallback(() => {
    const taskMapping = getValues('mappings.task')
    const sessionMapping = getValues('mappings.session')
    const additionalObjectsPath = 'mappings.additionalObjects' as const

    const customTaskMapping = taskMapping?.mappingType === MappingType.CUSTOM ? (taskMapping as DetailedCRMAccountIntegrationMapping) : null
    const customSessionMapping = sessionMapping?.mappingType === MappingType.CUSTOM ? (sessionMapping as DetailedCRMAccountIntegrationMapping) : null
    const additionalObjects = (getValues(additionalObjectsPath) as DetailedCRMAccountIntegrationMapping[]) || ([] as DetailedCRMAccountIntegrationMapping[])
    const filteredAdditionalObjects = additionalObjects.filter(
      (obj) => !obj?.additionalInformation?.isSessionObject && !obj?.additionalInformation?.isTaskObject,
    )

    if (!customTaskMapping && !customSessionMapping) {
      if (filteredAdditionalObjects.length !== additionalObjects.length) {
        setValue(additionalObjectsPath, filteredAdditionalObjects, { shouldDirty: false, shouldValidate: true })
      }
      return
    }

    const currentTaskObjectLimit = additionalObjects.find((obj) => obj?.additionalInformation?.isTaskObject)?.limit || DEFAULT_ADDITIONAL_OBJECTS_LIMIT
    const currentSessionObjectLimit = additionalObjects.find((obj) => obj?.additionalInformation?.isSessionObject)?.limit || DEFAULT_ADDITIONAL_OBJECTS_LIMIT

    const isSessionSameAsTask =
      customTaskMapping &&
      customSessionMapping &&
      customTaskMapping.objectName === customSessionMapping.objectName &&
      customTaskMapping.isHousehold == customSessionMapping.isHousehold

    if (!isSessionSameAsTask) {
      const currentTaskAdditionalObject = additionalObjects.find((obj) => obj?.additionalInformation?.isTaskObject)
      const currentSessionAdditionalObject = additionalObjects.find((obj) => obj?.additionalInformation?.isSessionObject)

      const updatedTaskAdditionalObject: DetailedCRMAccountIntegrationMapping | null = customTaskMapping
        ? {
            ...customTaskMapping,
            limit: currentTaskObjectLimit,
            additionalInformation: { ...(customTaskMapping?.additionalInformation || {}), isTaskObject: true },
          }
        : null

      const updatedSessionAdditionalObject: DetailedCRMAccountIntegrationMapping | null = customSessionMapping
        ? {
            ...customSessionMapping,
            limit: currentSessionObjectLimit,
            additionalInformation: { ...(customSessionMapping?.additionalInformation || {}), isSessionObject: true },
          }
        : null

      const shouldUpdate =
        !areMappingsEqual(currentTaskAdditionalObject, updatedTaskAdditionalObject) ||
        !areMappingsEqual(currentSessionAdditionalObject, updatedSessionAdditionalObject)

      if (!shouldUpdate) {
        return
      }

      const updatedAdditionalObjects = [
        ...filteredAdditionalObjects,
        ...(updatedTaskAdditionalObject ? [updatedTaskAdditionalObject] : []),
        ...(updatedSessionAdditionalObject ? [updatedSessionAdditionalObject] : []),
      ]
      setValue(additionalObjectsPath, updatedAdditionalObjects, { shouldDirty: false, shouldValidate: true })
      return
    }

    const currentSessionAndTaskObjectLimit =
      additionalObjects.find((obj) => obj?.additionalInformation?.isSessionObject && obj?.additionalInformation?.isTaskObject)?.limit ||
      DEFAULT_ADDITIONAL_OBJECTS_LIMIT

    const currentSessionAndTaskAdditionalObject = additionalObjects.find(
      (obj) => obj?.additionalInformation?.isSessionObject && obj?.additionalInformation?.isTaskObject,
    )
    const updatedSessionAndTaskAdditionalObject: DetailedCRMAccountIntegrationMapping = {
      ...customTaskMapping,
      limit: currentSessionAndTaskObjectLimit,
      additionalInformation: { ...(customTaskMapping?.additionalInformation || {}), isSessionObject: true, isTaskObject: true },
      fields: { ...customSessionMapping?.fields, ...(customTaskMapping?.fields || {}) },
      constraints: { ...customSessionMapping?.constraints, ...(customTaskMapping?.constraints || {}) },
    }
    if (areMappingsEqual(currentSessionAndTaskAdditionalObject, updatedSessionAndTaskAdditionalObject)) {
      return
    }
    const updatedAdditionalObjects = customTaskMapping ? [...filteredAdditionalObjects, updatedSessionAndTaskAdditionalObject] : filteredAdditionalObjects
    setValue(additionalObjectsPath, updatedAdditionalObjects, { shouldDirty: false, shouldValidate: true })
  }, [getValues, setValue])

  return syncAdditionalObjects
}

export const useGetEnabledWorkflows = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: workflows, isFetching: isWorkflowFetching, isLoading: isWorkflowLoading } = useGetAllEnabledWorkflowsQuery({ accountId })

  const businessEventExportWorkflow = useMemo(
    () => workflows?.data.find((workflow) => workflow.workflowId === WORKFLOWS.businessEventExport.id) ?? null,
    [workflows],
  )

  const businessEventExportWorkflowContext = useMemo(
    () => (businessEventExportWorkflow ? (businessEventExportWorkflow.context as BusinessEventExportWorkflowContext) : null),
    [businessEventExportWorkflow],
  )

  const businessEventExportWorkflowId = useMemo(() => (businessEventExportWorkflow ? businessEventExportWorkflow.id : null), [businessEventExportWorkflow])

  return {
    isWorkflowFetching,
    isWorkflowLoading,
    businessEventExportWorkflowContext,
    businessEventExportWorkflowId,
  }
}
